import * as THREE from 'three'

import Camera from './core/Camera.js'
import Renderer from './core/Renderer.js'
import Sizes from './utils/Sizes.js'
import Time from './utils/Time.js'
import Debug from './utils/Debug.js'
import Interaction from './utils/Interaction.js'
import Router from './utils/Router.js'
import AssetLoader from './utils/AssetLoader.js'
import Viewport from './utils/Viewport.js'
import { createGalleryLenis, createGlobalLenis } from './utils/Lenis.js'
import Director from './Director.js'

let instance = null

/**
 * 應用程式全域單例核心 (Application Core Singleton)
 * 負責實例化、持有與協調所有基礎服務 (Core Services) 與渲染迴圈
 */
export default class Producer {
    constructor(canvas) {
        // 確保全域單例 (Singleton Pattern)
        if (instance) {
            return instance
        }
        instance = this

        // 1. 基礎 Canvas & Scene 建立
        this.canvas = canvas
        this.scene = new THREE.Scene()

        // 2. 基礎服務與工具模組初始化
        this.debug = new Debug()
        this.sizes = new Sizes()
        this.time = new Time()
        this.camera = new Camera()
        this.viewport = new Viewport()
        this.renderer = new Renderer()

        // 3. 抓取 DOM Overlay 節點
        this.loader = document.querySelector('app-loader')
        this.header = document.querySelector('app-header')
        this.about = document.querySelector('app-about')
        this.project = document.querySelector('app-project')
        this.detail = document.querySelector('app-detail')
        this.footer = document.querySelector('app-footer')

        // 順序不可反過來，見 utils/Lenis.js 的說明
        this.detail.setLenis(createGalleryLenis(this.detail.getGalleryEl(), this.detail.getGalleryTrackEl()))

        // 需早於 Interaction 建立，供其消費，而非各自 new 一份
        this.lenis = createGlobalLenis()

        this.interaction = new Interaction()
        this.router = new Router()
        this.assetLoader = new AssetLoader()

        // 4. 導演機關 (轉場與核心業務邏輯)
        this.director = new Director()

        // 4.1 將 Director 顯式注入需要觸發導演動作的 Overlay 元件，取代全域 window.studio 存取
        this.detail.setDirector(this.director)
        this.footer.setDirector(this.director)
        this.header.setDirector(this.director)

        // 5. 綁定時間與尺寸事件
        this._bindEvents()
    }

    /** 綁定全域時間脈衝與 Resize 廣播 */
    _bindEvents() {
        this.time.onTick = () => {
            this.update()
        }
        this.sizes.onResize = () => {
            this.resize()
        }
    }

    /** 響應式尺寸切換鏈 */
    resize() {
        this.camera.resize()
        this.renderer.resize()
        this.director?.resize()
        this.renderer.update()
    }

    /** 渲染主迴圈 (Render Loop Multi-Pass Update) */
    update() {
        this.debug.stats?.begin()

        // 1. 互動與業務邏輯更新
        this.interaction.update()
        this.director.update()

        // 2. 3D 相機與 Debug 輔助線更新
        this.camera.update()
        this.debug.update() // 在 camera/carousel 更新後重算 debug 視覺（弧線 + bake）

        // 3. WebGL 畫面繪製
        this.renderer.update()

        this.debug.stats?.end()
    }

    /**
     * 完全銷毀應用程式實例
     * 清理所有事件監聽、GPU 貼圖/幾何體與記憶體，防止 Vite HMR 熱重載記憶體洩漏
     */
    dispose() {
        // 1. 停止主時間迴圈
        this.time.stop()

        // 2. 銷毀 Director 與 3D 場景內容 (Geometry/Material)
        this.director?.destroy?.()

        // 3. 銷毀基礎服務事件監聽
        this.sizes.destroy?.()
        this.interaction.destroy?.()
        this.router.destroy?.()
        this.debug.destroy?.()
        this.lenis.destroy()

        // 4. 釋放 GPU 資源 (VRAM)
        this.assetLoader.dispose()
        this.renderer.instance.dispose()

        // 5. 重置單例
        instance = null
    }
}
